import { Injectable, Injector, NgZone } from '@angular/core';
import { BaseService } from '@mobilefirstdev/base-angular';
import { WebSocketService } from './web-socket.service';
import { WebSocketMessageContextConnectionManager } from '../models/web-sockets/contexts/web-socket-message-context-connection-manager';
import { WebSocketPong } from '../models/web-sockets/messages/receive/web-socket-pong';
import { distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { ModalAppOutOfDate } from '../modals/modal-app-out-of-date';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { AppOutOfDateUtils } from '../utils/app-out-of-date-utils';

type AppIsOutOfDateData = { appIsOutOfDate: boolean; message: string };

@Injectable()
export class AppOutOfDateService extends BaseService {
  constructor(
    private injector: Injector,
    private ngbModal: NgbModal,
    private ngZone: NgZone,
    private webSocketService: WebSocketService
  ) {
    super();
    this.appIsOutOfDateData$.subscribeWhileAlive({
      owner: this,
      next: ({ appIsOutOfDate, message }) => appIsOutOfDate && this.openAppOutOfDateModal(message)
    });
  }

  // @ts-ignore
  private readonly appVersion = (require('package.json')?.version as string)?.replace(/-[a-zA-Z0-9]+/, '');

  protected readonly appIsOutOfDateData$: Observable<AppIsOutOfDateData> = this.webSocketService
    .getRelevantMessage$<WebSocketPong>(WebSocketMessageContextConnectionManager.Pong)
    .pipe(
      map(message => this.determineIfAppIsOutOfDate(message)),
      startWith({ appIsOutOfDate: false, message: null }),
      distinctUntilChanged((a, b) => a?.appIsOutOfDate === b?.appIsOutOfDate),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  public readonly appIsOutOfDate$ = this.appIsOutOfDateData$.pipe(map(({ appIsOutOfDate }) => appIsOutOfDate));

  private determineIfAppIsOutOfDate(msg: WebSocketPong): AppIsOutOfDateData {
    const [minMajor, minMinor, minPatch] = msg?.payload?.minSupportedVersion?.split('.') || [null, null, null];
    if (!minMajor && !minMinor && !minPatch) {
      return { appIsOutOfDate: false, message: null };
    }
    const [appMajor, appMinor, appPatch] = this.appVersion?.split('.') || [null, null, null];
    const valid = AppOutOfDateUtils.validVersionFromAllFragments(
      [minMajor, minMinor, minPatch],
      [appMajor, appMinor, appPatch]
    );
    return {
      appIsOutOfDate: !valid,
      message: msg?.payload?.message
    };
  }

  public openAppOutOfDateModal(desc: string): void {
    ModalAppOutOfDate.open(this.ngZone, this.ngbModal, this.injector, desc);
  }
}
